/**
 * Size & color chip options for all listing types (marketplace, wedding, designer, boutique).
 * Reuses wedding palettes/profiles and extends them for pre-owned / shop categories.
 */

import {
  WEDDING_COLOR_SETS,
  WEDDING_SIZE_PROFILES,
  WEDDING_CATEGORY_ATTRIBUTE_MAP,
} from './weddingPreLovedAttributes';
import { FILTER_COLORS } from './itemBrowseFilters';

export const LISTING_COLOR_SETS = {
  ...WEDDING_COLOR_SETS,
  general: [
    'Black',
    'White',
    'Ivory',
    'Cream',
    'Beige',
    'Grey',
    'Light Grey',
    'Charcoal',
    'Navy',
    'Blue',
    'Green',
    'Olive',
    'Brown',
    'Tan',
    'Red',
    'Burgundy',
    'Pink',
    'Purple',
    'Yellow',
    'Orange',
    'Gold',
    'Silver',
    'Multi',
    'Not applicable',
    'Other',
  ],
};

export const LISTING_SIZE_PROFILES = {
  ...WEDDING_SIZE_PROFILES,
  apparel_unisex: {
    label: 'Clothing size',
    hint: 'Select every size you have available (buyers often filter by size).',
    groups: [
      {
        label: 'Letter sizes',
        sizes: ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'],
      },
      {
        label: 'Numeric',
        sizes: ['0', '2', '4', '6', '8', '10', '12', '14', '16', '18', '20', '22', '24'],
      },
    ],
  },
  shoes_unisex: {
    label: 'Shoe size',
    hint: 'Select EU (and any other) sizes that apply.',
    groups: [
      {
        label: 'EU',
        sizes: ['35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47', '48'],
      },
      {
        label: 'US',
        sizes: ['5', '6', '7', '8', '9', '10', '11', '12', '13'],
      },
      {
        label: 'UK',
        sizes: ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
      },
    ],
  },
  bags: {
    label: 'Bag size',
    hint: 'Pick the size that best matches this bag.',
    groups: [
      {
        label: '',
        sizes: ['Mini', 'Small', 'Medium', 'Large', 'XL', 'One Size'],
      },
    ],
  },
  watches: {
    label: 'Watch / band size',
    hint: 'Case or strap size if known.',
    groups: [
      {
        label: '',
        sizes: ['Women', 'Men', 'Unisex', 'Small', 'Medium', 'Large', 'One Size'],
      },
    ],
  },
  kids: {
    label: 'Kids size',
    hint: 'Select age or clothing size.',
    groups: [
      {
        label: 'Age',
        sizes: ['0-3M', '3-6M', '6-12M', '12-18M', '18-24M', '2Y', '3Y', '4Y', '5Y', '6Y', '7Y', '8Y', '10Y', '12Y', '14Y'],
      },
      {
        label: 'Letter',
        sizes: ['XS', 'S', 'M', 'L', 'XL'],
      },
    ],
  },
};

/** @type {Record<string, { colorSet: string, sizeProfile: string|null }>} */
export const LISTING_CATEGORY_ATTRIBUTE_MAP = {
  ...WEDDING_CATEGORY_ATTRIBUTE_MAP,

  // Pre-owned / luxury marketplace
  Clothing: { colorSet: 'general', sizeProfile: 'apparel_unisex' },
  Footwear: { colorSet: 'general', sizeProfile: 'shoes_unisex' },
  Bags: { colorSet: 'general', sizeProfile: 'bags' },
  Accessories: { colorSet: 'general', sizeProfile: 'accessories' },
  Watches: { colorSet: 'general', sizeProfile: 'watches' },
  Luggage: { colorSet: 'general', sizeProfile: 'bags' },
  Baby: { colorSet: 'general', sizeProfile: 'kids' },
  'Beauty & Personal Care': { colorSet: 'general', sizeProfile: 'accessories' },
  'Sports & Outdoors': { colorSet: 'general', sizeProfile: 'apparel_unisex' },
  'Home & Kitchen': { colorSet: 'decor', sizeProfile: null },
  'Arts & Crafts': { colorSet: 'general', sizeProfile: null },
  Stationary: { colorSet: 'general', sizeProfile: null },
  'Musical Instruments': { colorSet: 'general', sizeProfile: null },
  'Computers & Electronics': { colorSet: 'neutral', sizeProfile: null },
  'Toys & Video Games': { colorSet: 'general', sizeProfile: null },
  Books: { colorSet: 'neutral', sizeProfile: null },
  'Pet Supplies': { colorSet: 'general', sizeProfile: null },
  Automotive: { colorSet: 'neutral', sizeProfile: null },
  'Tools & Home Improvement': { colorSet: 'neutral', sizeProfile: null },
  Other: { colorSet: 'general', sizeProfile: 'accessories' },

  // Designer / boutique shop catalog
  Fashion: { colorSet: 'general', sizeProfile: 'apparel_unisex' },
  Jewelry: { colorSet: 'accessories', sizeProfile: 'accessories' },
  Nails: { colorSet: 'general', sizeProfile: 'accessories' },
  'Vintage Collections': { colorSet: 'general', sizeProfile: 'apparel_unisex' },
  Tableware: { colorSet: 'decor', sizeProfile: null },
  'Home Decor': { colorSet: 'decor', sizeProfile: null },
  'Art & Paintings': { colorSet: 'decor', sizeProfile: null },
  'Shop Catalog': { colorSet: 'general', sizeProfile: 'accessories' },
};

/**
 * @param {string} category
 * @returns {{ colors: string[], sizeProfile: object|null, requiresSize: boolean }|null}
 */
export function getListingSizeColorAttributes(category) {
  if (!category) return null;
  const map = LISTING_CATEGORY_ATTRIBUTE_MAP[category] || {
    colorSet: 'general',
    sizeProfile: 'accessories',
  };
  const colors = map.colorSet === 'general'
    ? [...FILTER_COLORS]
    : [...(LISTING_COLOR_SETS[map.colorSet] || FILTER_COLORS)];
  const sizeProfile = map.sizeProfile ? LISTING_SIZE_PROFILES[map.sizeProfile] : null;
  return {
    colors,
    sizeProfile,
    requiresSize: Boolean(sizeProfile),
  };
}

/**
 * @param {string} color
 * @param {string[]} selectedSizes
 * @returns {{ color: string, size: string|undefined, available_sizes: string }}
 */
export function buildSizeColorPayload(color, selectedSizes = []) {
  const sizes = Array.isArray(selectedSizes) ? selectedSizes.filter(Boolean) : [];
  return {
    color: color || undefined,
    size: sizes[0] || undefined,
    available_sizes: sizes.length ? sizes.join(', ') : undefined,
  };
}

/** Parse stored available_sizes / size back into a chip selection array. */
export function parseAvailableSizes(availableSizes, size) {
  if (availableSizes && String(availableSizes).trim()) {
    return String(availableSizes)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (size && String(size).trim()) return [String(size).trim()];
  return [];
}
